import { useCallback, useEffect, useRef, useState } from 'react';
import type { LscVodCandidate } from '../types';
import {
  truthVerificationFailed,
  truthVerificationInProgress,
  truthVerificationPollComplete,
  VOD_TRUTH_VERIFICATION_MAX_POLL_ATTEMPTS,
  VOD_TRUTH_VERIFICATION_POLL_INTERVAL_MS,
} from '../vodTruthVerificationPolling';

interface UseVodTruthVerificationPollingOptions {
  candidate: LscVodCandidate;
  onRefresh: () => Promise<void>;
}

export function useVodTruthVerificationPolling({
  candidate,
  onRefresh,
}: UseVodTruthVerificationPollingOptions) {
  const [active, setActive] = useState(() => truthVerificationInProgress(candidate));
  const [pollError, setPollError] = useState<string | null>(null);
  const attemptsRef = useRef(0);

  const startPolling = useCallback(() => {
    attemptsRef.current = 0;
    setPollError(null);
    setActive(true);
  }, []);

  const clearPollError = useCallback(() => {
    setPollError(null);
  }, []);

  useEffect(() => {
    if (truthVerificationInProgress(candidate)) {
      setActive(true);
    }
  }, [candidate.truth_verification_status, candidate.truth_checked_at]);

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    if (truthVerificationPollComplete(candidate)) {
      setActive(false);
      if (truthVerificationFailed(candidate)) {
        setPollError('Verification failed. Try again in a moment.');
      }
      return undefined;
    }

    let cancelled = false;
    const intervalId = window.setInterval(() => {
      if (cancelled || document.hidden) {
        return;
      }

      attemptsRef.current += 1;
      if (attemptsRef.current > VOD_TRUTH_VERIFICATION_MAX_POLL_ATTEMPTS) {
        setActive(false);
        setPollError(
          'Verification is taking longer than expected. Refresh the page or try again.'
        );
        return;
      }

      void onRefresh().catch(() => {
        if (!cancelled) {
          setPollError('Could not refresh verification status.');
        }
      });
    }, VOD_TRUTH_VERIFICATION_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [active, candidate, onRefresh]);

  const polling = active && truthVerificationInProgress(candidate);

  return {
    polling,
    pollError,
    startPolling,
    clearPollError,
  };
}
